/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { GET_LABEL_PDF, GET_LABEL_STATUS } from "../graphql";

const is = require("is_js");
const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IGetLabelPDFMethods extends IPluginMethodMap {
  getLabelPDF(args: any): Promise<any>;
}

export class GetLabelPDFService implements IAgentPlugin {
  readonly methods: IGetLabelPDFMethods;
  private pollingTime = 10000;

  constructor() {
    this.methods = {
      getLabelPDF: this.getLabelPDF.bind(this),
    };
  }

  async getLabelPDF(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const res = await client.query({
          query: GET_LABEL_PDF,
          variables: {
            input: {
              presentation: args?.presentation,
              section: args?.section,
              sectionOptType: args?.sectionOptType,
              downloadAnyway: args?.downloadAnyway ?? false,
              downloadPartial: args?.downloadPartial ?? false,
            },
          },
          fetchPolicy: "network-only",
        });

        const result = res?.data?.getLabelPDF;

        if (
          result?.message &&
          is.existy(result?.message) &&
          (result?.message === "No" || result?.message === "Partial")
        ) {
          return result;
        } else if (result?.jobId && is.existy(result?.jobId)) {
          return this.checkStatus(result?.jobId);
        } else {
          throw new Error("Some unexpected error occurred");
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private async checkStatus(jobId: string, retries = 25) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      const res = await client.query({
        query: GET_LABEL_STATUS,
        variables: {
          jobId: jobId,
        },
        fetchPolicy: "network-only",
      });

      if (res?.data?.getLabelStatus?.message === "success") {
        return res?.data?.getLabelStatus?.pdfUrl;
      } else if (res?.data?.getLabelStatus?.message === "pending" && res?.data?.getLabelStatus?.message === "PaymentNotComplete") {
        if (retries > 0) {
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              this.checkStatus(jobId, retries - 1)
                .then(resolve)
                .catch(reject);
            }, this.pollingTime || 10000);
          });
        } else {
          throw new Error("Exceeded retry limit for checking status");
        }
      } else {
        throw new Error("Some unexpected error occurred");
      }
    } catch (err: any) {
      console.error("checkStatus error ", err);

      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
}
